/**
 * Gradient Converter
 *
 * Converts gradient configurations to Konva-compatible gradient properties.
 * Includes LRU cache for expensive pixel-rendered gradients (diamond, spiral, plasma, ripple).
 */

import type { GradientConfig } from '../../types/richText';

// ---------------------------------------------------------------------------
// Gradient canvas cache — avoids re-rendering identical pixel gradients.
// Key = JSON hash of gradient config + dimensions. Holds last 8 results.
// ---------------------------------------------------------------------------
const GRADIENT_CACHE_MAX = 8;
const _gradientCache: Array<{ key: string; result: any }> = [];

function gradientCacheKey(gradient: GradientConfig, w: number, h: number): string {
  return JSON.stringify({ ...gradient, _w: w, _h: h });
}

function getCachedGradient(key: string): any | null {
  const entry = _gradientCache.find((e) => e.key === key);
  if (entry) {
    // Move to end (most recently used)
    const idx = _gradientCache.indexOf(entry);
    if (idx < _gradientCache.length - 1) {
      _gradientCache.splice(idx, 1);
      _gradientCache.push(entry);
    }
    return entry.result;
  }
  return null;
}

function setCachedGradient(key: string, result: any): void {
  if (_gradientCache.length >= GRADIENT_CACHE_MAX) {
    _gradientCache.shift(); // evict oldest
  }
  _gradientCache.push({ key, result });
}

/**
 * Convert linear gradient to Konva props
 * Handles angle rotation and element dimensions
 */
export function convertLinearGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number,
  elementRotation: number = 0
): any {
  if (gradient.type !== 'linear') {
    throw new Error('convertLinearGradientToKonva expects a linear gradient');
  }

  // Effective angle = gradient angle + element rotation
  const angle = (gradient.angle || 0) + elementRotation;
  const angleRad = (angle * Math.PI) / 180;

  // Calculate gradient vector based on angle
  // 0° = left to right
  // 90° = top to bottom
  // 180° = right to left
  // 270° = bottom to top

  const centerX = width / 2;
  const centerY = height / 2;

  // Calculate the maximum distance from center to corner
  const maxDist = Math.sqrt(centerX ** 2 + centerY ** 2);

  // Calculate start and end points
  const startX = centerX - Math.cos(angleRad) * maxDist;
  const startY = centerY - Math.sin(angleRad) * maxDist;
  const endX = centerX + Math.cos(angleRad) * maxDist;
  const endY = centerY + Math.sin(angleRad) * maxDist;

  // Build color stops array for Konva (alternates: offset, color, offset, color, ...)
  const colorStops: Array<number | string> = [];
  gradient.stops.forEach((stop) => {
    colorStops.push(stop.offset, stop.color);
  });

  return {
    fillLinearGradientStartPoint: { x: startX, y: startY },
    fillLinearGradientEndPoint: { x: endX, y: endY },
    fillLinearGradientColorStops: colorStops,
  };
}

/**
 * Convert radial gradient to Konva props
 */
export function convertRadialGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  if (gradient.type !== 'radial') {
    throw new Error('convertRadialGradientToKonva expects a radial gradient');
  }

  const centerX = (gradient.centerX || 0.5) * width;
  const centerY = (gradient.centerY || 0.5) * height;

  // Calculate radius (distance to farthest corner)
  const radius = Math.max(
    Math.sqrt(centerX ** 2 + centerY ** 2),
    Math.sqrt((width - centerX) ** 2 + centerY ** 2),
    Math.sqrt(centerX ** 2 + (height - centerY) ** 2),
    Math.sqrt((width - centerX) ** 2 + (height - centerY) ** 2)
  );

  // Build color stops array for Konva (alternates: offset, color, offset, color, ...)
  const colorStops: Array<number | string> = [];
  gradient.stops.forEach((stop) => {
    colorStops.push(stop.offset, stop.color);
  });

  return {
    fillRadialGradientStartPoint: { x: centerX, y: centerY },
    fillRadialGradientEndPoint: { x: centerX, y: centerY },
    fillRadialGradientStartRadius: 0,
    fillRadialGradientEndRadius: radius,
    fillRadialGradientColorStops: colorStops,
  };
}

/**
 * Render a conic gradient to an offscreen canvas and return as fillPatternImage
 */
export function convertConicGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  if (gradient.type !== 'conic') {
    throw new Error('convertConicGradientToKonva expects a conic gradient');
  }

  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const cx = (gradient.centerX ?? 0.5) * w;
  const cy = (gradient.centerY ?? 0.5) * h;
  const startAngle = ((gradient.angle ?? 0) * Math.PI) / 180;

  const conicGrad = ctx.createConicGradient(startAngle, cx, cy);
  gradient.stops.forEach((stop) => {
    conicGrad.addColorStop(Math.max(0, Math.min(1, stop.offset)), stop.color);
  });

  ctx.fillStyle = conicGrad;
  ctx.fillRect(0, 0, w, h);

  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Render a diamond gradient to an offscreen canvas and return as fillPatternImage
 */
export function convertDiamondGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  if (gradient.type !== 'diamond') {
    throw new Error('convertDiamondGradientToKonva expects a diamond gradient');
  }

  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const cx = (gradient.centerX ?? 0.5) * w;
  const cy = (gradient.centerY ?? 0.5) * h;
  const maxDist = Math.max(cx, w - cx) + Math.max(cy, h - cy);

  // Diamond distance = |dx| + |dy| (Manhattan distance)
  // Render pixel-by-pixel for accurate diamond shape
  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;

  // Pre-parse stop colors to RGBA
  const parsedStops = gradient.stops.map((s) => {
    const c = parseHexToRGBA(s.color);
    return { offset: s.offset, ...c };
  });

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dist = Math.abs(x - cx) + Math.abs(y - cy);
      const t = maxDist > 0 ? Math.min(1, dist / maxDist) : 0;
      const { r, g, b, a } = interpolateStops(parsedStops, t);
      const idx = (y * w + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(a * 255);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

function parseHexToRGBA(color: string): { r: number; g: number; b: number; a: number } {
  // Handle hex
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    const r = parseInt(hex.slice(0, 2), 16) || 0;
    const g = parseInt(hex.slice(2, 4), 16) || 0;
    const b = parseInt(hex.slice(4, 6), 16) || 0;
    const a = hex.length >= 8 ? (parseInt(hex.slice(6, 8), 16) || 0) / 255 : 1;
    return { r, g, b, a };
  }
  // Handle rgba(r,g,b,a)
  const m = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i);
  if (m) {
    return {
      r: parseInt(m[1], 10),
      g: parseInt(m[2], 10),
      b: parseInt(m[3], 10),
      a: m[4] ? parseFloat(m[4]) : 1,
    };
  }
  return { r: 0, g: 0, b: 0, a: 1 };
}

function interpolateStops(
  stops: Array<{ offset: number; r: number; g: number; b: number; a: number }>,
  t: number
): { r: number; g: number; b: number; a: number } {
  if (stops.length === 0) return { r: 0, g: 0, b: 0, a: 1 };
  if (t <= stops[0].offset) return stops[0];
  if (t >= stops[stops.length - 1].offset) return stops[stops.length - 1];

  for (let i = 0; i < stops.length - 1; i++) {
    const s0 = stops[i];
    const s1 = stops[i + 1];
    if (t >= s0.offset && t <= s1.offset) {
      const range = s1.offset - s0.offset;
      const f = range > 0 ? (t - s0.offset) / range : 0;
      return {
        r: Math.round(s0.r + (s1.r - s0.r) * f),
        g: Math.round(s0.g + (s1.g - s0.g) * f),
        b: Math.round(s0.b + (s1.b - s0.b) * f),
        a: s0.a + (s1.a - s0.a) * f,
      };
    }
  }
  return stops[stops.length - 1];
}

/**
 * Render a spiral gradient — ViewsBoost original.
 * Colors spiral outward from center like a galaxy arm.
 * Math: t = (angle/(2π) + distance * twist) mod 1
 */
export function convertSpiralGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const cx = (gradient.centerX ?? 0.5) * w;
  const cy = (gradient.centerY ?? 0.5) * h;
  const twist = gradient.twist ?? 3;
  const startAngle = ((gradient.angle ?? 0) * Math.PI) / 180;
  const maxDist = Math.sqrt(Math.max(cx, w - cx) ** 2 + Math.max(cy, h - cy) ** 2);

  const parsedStops = gradient.stops.map((s) => ({
    offset: s.offset,
    ...parseHexToRGBA(s.color),
  }));

  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx) + startAngle;
      const normalizedDist = maxDist > 0 ? dist / maxDist : 0;

      // Spiral formula: combine angle position with distance-based twist
      let t = (angle / (2 * Math.PI) + normalizedDist * twist) % 1;
      if (t < 0) t += 1;

      const { r, g, b, a } = interpolateStops(parsedStops, t);
      const idx = (y * w + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(a * 255);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Render a plasma gradient — ViewsBoost original.
 * Overlapping sine waves create organic, flowing color blobs.
 * Math: v = sin(x·f) + sin(y·f) + sin((x+y)·f) + sin(√(x²+y²)·f)
 */
export function convertPlasmaGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const freq = (gradient.frequency ?? 5) * 0.5;
  const phaseRad = ((gradient.phase ?? 0) * Math.PI) / 180;
  const turb = (gradient.turbulence ?? 50) / 100;

  const parsedStops = gradient.stops.map((s) => ({
    offset: s.offset,
    ...parseHexToRGBA(s.color),
  }));

  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const nx = x / w;
      const ny = y / h;

      // 4 overlapping sine waves for the plasma effect
      const v1 = Math.sin((nx * freq * Math.PI * 2) + phaseRad);
      const v2 = Math.sin((ny * freq * Math.PI * 2) + phaseRad * 0.7);
      const v3 = Math.sin(((nx + ny) * freq * Math.PI) + phaseRad * 1.3) * turb;
      const v4 = Math.sin((Math.sqrt(nx * nx + ny * ny) * freq * Math.PI * 2) + phaseRad * 0.5) * turb;

      // Combine and normalize to 0-1
      const raw = v1 + v2 + v3 + v4;
      const maxPossible = 2 + 2 * turb;
      const t = Math.max(0, Math.min(1, (raw + maxPossible) / (2 * maxPossible)));

      const { r, g, b, a } = interpolateStops(parsedStops, t);
      const idx = (y * w + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(a * 255);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Render a ripple gradient — ViewsBoost original.
 * Concentric rings that oscillate through colors like a stone dropped in water.
 * Math: t = |sin(distance × frequency × π)| with optional decay
 */
export function convertRippleGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const cx = (gradient.centerX ?? 0.5) * w;
  const cy = (gradient.centerY ?? 0.5) * h;
  const freq = gradient.frequency ?? 5;
  const decayFactor = (gradient.decay ?? 30) / 100;
  const maxDist = Math.sqrt(Math.max(cx, w - cx) ** 2 + Math.max(cy, h - cy) ** 2);

  const parsedStops = gradient.stops.map((s) => ({
    offset: s.offset,
    ...parseHexToRGBA(s.color),
  }));

  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const normalizedDist = maxDist > 0 ? dist / maxDist : 0;

      // Oscillating rings: sin wave along distance
      const wave = Math.sin(normalizedDist * freq * Math.PI * 2);
      // Normalize wave from [-1,1] to [0,1]
      let t = (wave + 1) / 2;

      // Apply decay: rings fade toward edges
      if (decayFactor > 0) {
        const envelope = Math.exp(-normalizedDist * decayFactor * 5);
        t = t * envelope + (1 - envelope) * 0.5;
      }

      t = Math.max(0, Math.min(1, t));

      const { r, g, b, a } = interpolateStops(parsedStops, t);
      const idx = (y * w + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(a * 255);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Render a satin gradient — ViewsBoost original.
 * Simulates light hitting satin fabric: a soft elliptical gaussian highlight
 * over a rich base color. The highlight is directionally elongated along an angle.
 * Math: intensity = exp(-((dx_rot²/σx² + dy_rot²/σy²) / 2))
 */
export function convertSatinGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  // Light position (where the highlight is centered)
  const cx = (gradient.centerX ?? 0.3) * w;
  const cy = (gradient.centerY ?? 0.2) * h;

  // Direction of the satin sheen (elongates the highlight along this angle)
  const angleRad = ((gradient.angle ?? 135) * Math.PI) / 180;
  const cosA = Math.cos(angleRad);
  const sinA = Math.sin(angleRad);

  // Spread controls gaussian sigma — higher = wider/softer highlight
  const spreadVal = (gradient.spread ?? 50) / 100; // 0-1
  const maxDim = Math.max(w, h);
  // σx is wider (along the angle), σy is narrower (perpendicular) for elongated sheen
  const sigmaX = maxDim * (0.15 + spreadVal * 0.6);  // major axis
  const sigmaY = sigmaX * 0.5;                         // minor axis (half for elongation)

  const parsedStops = gradient.stops.map((s) => ({
    offset: s.offset,
    ...parseHexToRGBA(s.color),
  }));

  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;

  // Pre-compute inverse sigma squared for speed
  const invSigmaX2 = 1 / (2 * sigmaX * sigmaX);
  const invSigmaY2 = 1 / (2 * sigmaY * sigmaY);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;

      // Rotate into the highlight's coordinate system
      const dxRot = dx * cosA + dy * sinA;
      const dyRot = -dx * sinA + dy * cosA;

      // Elliptical gaussian falloff
      const exponent = (dxRot * dxRot) * invSigmaX2 + (dyRot * dyRot) * invSigmaY2;
      const intensity = Math.exp(-exponent);

      // Map intensity to stop position: 1.0 (center) → offset 0, 0.0 (edge) → offset 1
      const t = Math.max(0, Math.min(1, 1 - intensity));

      const { r, g, b, a } = interpolateStops(parsedStops, t);
      const idx = (y * w + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(a * 255);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Convert gradient config to Konva props (auto-detects type)
 */
export function convertGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number,
  elementRotation: number = 0
): any {
  // Linear and radial use native Konva gradient props — fast, no caching needed
  if (gradient.type === 'linear') {
    return convertLinearGradientToKonva(gradient, width, height, elementRotation);
  } else if (gradient.type === 'radial') {
    return convertRadialGradientToKonva(gradient, width, height);
  }

  // All other types use expensive pixel rendering — use cache
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const key = gradientCacheKey(gradient, w, h);
  const cached = getCachedGradient(key);
  if (cached) return cached;

  let result: any;
  if (gradient.type === 'conic') {
    result = convertConicGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'diamond') {
    result = convertDiamondGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'spiral') {
    result = convertSpiralGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'plasma') {
    result = convertPlasmaGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'ripple') {
    result = convertRippleGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'satin') {
    result = convertSatinGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'mesh') {
    result = convertMeshGradientToKonva(gradient, width, height);
  } else if (gradient.type === 'sunburst') {
    result = convertSunburstGradientToKonva(gradient, width, height);
  } else {
    throw new Error(`Unknown gradient type: ${gradient.type}`);
  }

  setCachedGradient(key, result);
  return result;
}

// ---------------------------------------------------------------------------
// Mesh focal point positions (shared by renderer + CSS approximator)
// ---------------------------------------------------------------------------
const MESH_FOCAL_POSITIONS: Array<[number, number]> = [
  [0.15, 0.15], // top-left
  [0.85, 0.15], // top-right
  [0.85, 0.85], // bottom-right
  [0.15, 0.85], // bottom-left
  [0.50, 0.50], // center
  [0.50, 0.12], // top-center
  [0.88, 0.50], // right-center
  [0.50, 0.88], // bottom-center
  [0.12, 0.50], // left-center
  [0.30, 0.30], // inner top-left
  [0.70, 0.30], // inner top-right
  [0.70, 0.70], // inner bottom-right
];

export function getMeshFocalPoints(n: number): Array<{ x: number; y: number }> {
  return MESH_FOCAL_POSITIONS.slice(0, Math.max(2, Math.min(n, MESH_FOCAL_POSITIONS.length)))
    .map(([x, y]) => ({ x, y }));
}

/**
 * Render a mesh gradient — multi-color inverse-distance-weighted blend.
 * Each stop is anchored at a focal point; colors blend organically like a
 * Figma/iOS mesh gradient. No extra config needed — positions are automatic.
 */
export function convertMeshGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const stops = gradient.stops;
  if (stops.length < 2) return { fill: stops[0]?.color || '#cccccc' };

  const points = getMeshFocalPoints(stops.length);
  const parsedColors = stops.map((s) => parseHexToRGBA(s.color));

  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;
  const EPS = 0.0004; // prevents divide-by-zero at exact focal point

  for (let y = 0; y < h; y++) {
    const ny = y / h;
    for (let x = 0; x < w; x++) {
      const nx = x / w;

      // Inverse-distance-squared weights → natural blob-like blending
      let totalWeight = 0;
      const weights = points.map((p) => {
        const dx = nx - p.x;
        const dy = ny - p.y;
        const wt = 1 / (dx * dx + dy * dy + EPS);
        totalWeight += wt;
        return wt;
      });

      let r = 0, g = 0, b = 0;
      for (let i = 0; i < points.length; i++) {
        const wn = weights[i] / totalWeight;
        r += parsedColors[i].r * wn;
        g += parsedColors[i].g * wn;
        b += parsedColors[i].b * wn;
      }

      const idx = (y * w + x) * 4;
      data[idx]     = Math.round(r);
      data[idx + 1] = Math.round(g);
      data[idx + 2] = Math.round(b);
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Render a sunburst gradient — rays from a focal point.
 * Math: t = (angle / (2π)) mod (1/rays) * rays
 * Alternates between stops along the angular sweep.
 */
export function convertSunburstGradientToKonva(
  gradient: GradientConfig,
  width: number,
  height: number
): any {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { fill: '#cccccc' };

  const cx = (gradient.centerX ?? 0.5) * w;
  const cy = (gradient.centerY ?? 0.5) * h;
  const startAngle = ((gradient.angle ?? 0) * Math.PI) / 180;
  // Number of rays = frequency param (default 12), min 2
  const numRays = Math.max(2, gradient.frequency ?? 12);
  const rayWidth = (Math.PI * 2) / numRays;

  const parsedStops = gradient.stops.map((s) => ({
    offset: s.offset,
    ...parseHexToRGBA(s.color),
  }));

  const maxDist = Math.sqrt(Math.max(cx, w - cx) ** 2 + Math.max(cy, h - cy) ** 2);
  const imgData = ctx.createImageData(w, h);
  const data = imgData.data;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;

      // Angle position within one ray period → 0-1
      let angle = Math.atan2(dy, dx) - startAngle;
      angle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const rayPos = (angle % rayWidth) / rayWidth; // 0-1 within one ray

      // Smooth edge between rays using cosine blend
      const t = (1 - Math.cos(rayPos * Math.PI * 2)) / 2;

      // Optional: apply radial fade from center for glow effect
      const dist = Math.sqrt(dx * dx + dy * dy);
      const radialBlend = maxDist > 0 ? Math.min(1, dist / maxDist) : 0;

      // Blend: rays use angular t, with slight radial brightening at center
      const tFinal = t * 0.85 + radialBlend * 0.15;

      const { r, g, b, a } = interpolateStops(parsedStops, tFinal);
      const idx = (y * w + x) * 4;
      data[idx]     = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(a * 255);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return { fillPatternImage: canvas, fillPatternRepeat: 'no-repeat' };
}

/**
 * Preview gradient as CSS (for UI previews)
 */
export function gradientToCSS(gradient: GradientConfig): string {
  if (gradient.type === 'linear') {
    const angle = gradient.angle || 0;
    const stops = gradient.stops
      .map((stop) => `${stop.color} ${stop.offset * 100}%`)
      .join(', ');
    return `linear-gradient(${angle}deg, ${stops})`;
  } else if (gradient.type === 'radial') {
    const stops = gradient.stops
      .map((stop) => `${stop.color} ${stop.offset * 100}%`)
      .join(', ');
    return `radial-gradient(circle, ${stops})`;
  } else if (gradient.type === 'conic') {
    const angle = gradient.angle || 0;
    const cx = Math.round((gradient.centerX ?? 0.5) * 100);
    const cy = Math.round((gradient.centerY ?? 0.5) * 100);
    const stops = gradient.stops
      .map((stop) => `${stop.color} ${stop.offset * 100}%`)
      .join(', ');
    return `conic-gradient(from ${angle}deg at ${cx}% ${cy}%, ${stops})`;
  } else if (gradient.type === 'diamond') {
    const cx = Math.round((gradient.centerX ?? 0.5) * 100);
    const cy = Math.round((gradient.centerY ?? 0.5) * 100);
    const stops = gradient.stops
      .map((stop) => `${stop.color} ${stop.offset * 100}%`)
      .join(', ');
    const first = gradient.stops[0]?.color || 'transparent';
    return `conic-gradient(from 45deg at ${cx}% ${cy}%, ${stops}, ${first})`;
  } else if (gradient.type === 'spiral') {
    const angle = gradient.angle || 0;
    const cx = Math.round((gradient.centerX ?? 0.5) * 100);
    const cy = Math.round((gradient.centerY ?? 0.5) * 100);
    const stops = gradient.stops
      .map((stop) => `${stop.color} ${stop.offset * 100}%`)
      .join(', ');
    return `repeating-conic-gradient(from ${angle}deg at ${cx}% ${cy}%, ${stops})`;
  } else if (gradient.type === 'plasma' || gradient.type === 'ripple') {
    const stops = gradient.stops
      .map((stop) => `${stop.color} ${stop.offset * 100}%`)
      .join(', ');
    return `linear-gradient(135deg, ${stops})`;
  } else if (gradient.type === 'satin') {
    const cx = Math.round((gradient.centerX ?? 0.3) * 100);
    const cy = Math.round((gradient.centerY ?? 0.2) * 100);
    const highlight = gradient.stops[0]?.color || '#ffffff';
    const base = gradient.stops[gradient.stops.length - 1]?.color || '#000000';
    const spread = gradient.spread ?? 50;
    const size = 40 + spread * 0.6;
    return `radial-gradient(ellipse ${size}% ${Math.round(size * 0.7)}% at ${cx}% ${cy}%, ${highlight}, transparent), ${base}`;
  } else if (gradient.type === 'mesh') {
    // Approximate mesh with layered radial blobs at focal positions
    const points = getMeshFocalPoints(gradient.stops.length);
    const layers = gradient.stops.map((stop, i) => {
      const px = Math.round(points[i].x * 100);
      const py = Math.round(points[i].y * 100);
      return `radial-gradient(ellipse 65% 65% at ${px}% ${py}%, ${stop.color} 0%, transparent 70%)`;
    });
    const base = gradient.stops[Math.floor(gradient.stops.length / 2)]?.color || '#000000';
    return [...layers, base].join(', ');
  } else if (gradient.type === 'sunburst') {
    // Approximate sunburst with conic gradient
    const angle = gradient.angle ?? 0;
    const cx = Math.round((gradient.centerX ?? 0.5) * 100);
    const cy = Math.round((gradient.centerY ?? 0.5) * 100);
    const rays = gradient.frequency ?? 12;
    const colorA = gradient.stops[0]?.color || '#ffffff';
    const colorB = gradient.stops[gradient.stops.length - 1]?.color || '#000000';
    const step = 100 / rays;
    const conicStops: string[] = [];
    for (let i = 0; i < rays; i++) {
      const a = i % 2 === 0 ? colorA : colorB;
      conicStops.push(`${a} ${Math.round(i * step)}%`, `${a} ${Math.round((i + 1) * step)}%`);
    }
    return `conic-gradient(from ${angle}deg at ${cx}% ${cy}%, ${conicStops.join(', ')})`;
  }

  return 'transparent';
}
